import React, { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  getDocs, 
  updateDoc, 
  doc, 
  deleteDoc,
  query,
  orderBy 
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { SchoolClass, Student } from '../types';
import { getCachedClasses, getCachedStudents, dataCache } from '../services/dataCacheService';
import { useAcademicYear } from '../context/AcademicYearContext';
import { getStudentClassForYear } from '../services/academicYearService';
import { ClassStaffModal } from './ClassStaffModal';
import { EnrollStudentsModal } from './EnrollStudentsModal';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Check, 
  X, 
  Loader2, 
  School, 
  Search,
  Calendar,
  UserCheck,
  Users,
  UserPlus,
  Phone,
  Sparkles,
  ArrowUpRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

const Classes: React.FC = () => {
  const { selectedYear, currentYearObj, classConfigs, refreshClassConfigs } = useAcademicYear();
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newClassName, setNewClassName] = useState('');
  const [newClassColor, setNewClassColor] = useState('#4f46e5');
  const [newClassImage, setNewClassImage] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [editImage, setEditImage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Modals for academic year operations
  const [staffModalTarget, setStaffModalTarget] = useState<SchoolClass | null>(null);
  const [enrollModalTarget, setEnrollModalTarget] = useState<SchoolClass | null>(null);

  const luxuriousColors = [
    // Reds & Pinks
    '#991b1b', '#b91c1c', '#dc2626', '#9d174d', '#be185d', '#db2777',
    // Oranges & Yellows
    '#9a3412', '#c2410c', '#ea580c', '#92400e', '#b45309', '#d97706', '#f59e0b', '#fbbf24', '#facc15',
    // Greens
    '#166534', '#15803d', '#16a34a', '#22c55e', '#134e4a', '#115e59', '#0f766e', '#0d9488', '#3f6212', '#4d7c0f', '#65a30d',
    // Blues & Cyans
    '#1e3a8a', '#1d4ed8', '#2563eb', '#3b82f6', '#155e75', '#0e7490', '#0891b2', '#06b6d4',
    // Purples & Indigos
    '#312e81', '#4338ca', '#4f46e5', '#6366f1', '#581c87', '#6b21a8', '#7e22ce', '#9333ea',
    // Slates & Neutrals
    '#1e293b', '#334155', '#475569', '#0f172a'
  ];

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, isEdit: boolean) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 200 * 1024) { // 200KB limit for base64 in Firestore
        alert('התמונה גדולה מדי. אנא בחר תמונה קטנה מ-200KB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        if (isEdit) setEditImage(reader.result as string);
        else setNewClassImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchStudents();
  }, [selectedYear]);

  const fetchStudents = async () => {
    try {
      const list = await getCachedStudents();
      setAllStudents(list);
    } catch (e) {
      console.warn('Could not load students for counts:', e);
    }
  };

  const getEnrolledCount = (classId: string) => {
    return allStudents.filter(s => {
      const en = getStudentClassForYear(s, selectedYear);
      return en?.classId === classId;
    }).length;
  };

  const fetchClasses = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const classesData = await getCachedClasses(forceRefresh);
      setClasses(classesData.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he')));
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'classes');
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClass = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newClassName.trim();
    if (!name) return;

    // Check for duplicates
    if (classes.some(c => c.name === name)) {
      alert('כיתה בשם זה כבר קיימת במערכת');
      return;
    }

    setActionLoading(true);
    try {
      await addDoc(collection(db, 'classes'), {
        name: name,
        color: newClassColor,
        imageUrl: newClassImage,
        active: true
      });
      dataCache.remove('classes'); // Invalidate cache
      setNewClassName('');
      setNewClassColor('#4f46e5');
      setNewClassImage(null);
      setIsAdding(false);
      fetchClasses(true);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'classes');
      console.error('Error adding class:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateClass = async (id: string) => {
    const name = editName.trim();
    if (!name) return;

    // Check for duplicates (excluding current class)
    if (classes.some(c => c.name === name && c.id !== id)) {
      alert('כיתה בשם זה כבר קיימת במערכת');
      return;
    }

    setActionLoading(true);
    try {
      await updateDoc(doc(db, 'classes', id), {
        name: name,
        color: editColor,
        imageUrl: editImage
      });
      dataCache.remove('classes'); // Invalidate cache
      setEditingId(null);
      fetchClasses(true);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'classes');
      console.error('Error updating class:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleClassStatus = async (id: string, currentStatus: boolean) => {
    try {
      await updateDoc(doc(db, 'classes', id), {
        active: !currentStatus
      });
      dataCache.remove('classes'); // Invalidate cache
      fetchClasses(true);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'classes');
      console.error('Error toggling class status:', error);
    }
  };

  const handleDeleteClass = async (id: string) => {
    setActionLoading(true);
    try {
      await deleteDoc(doc(db, 'classes', id));
      dataCache.remove('classes'); // Invalidate cache
      fetchClasses(true);
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'classes');
      console.error('Error deleting class:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredClasses = classes.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Academic Year Info Banner */}
      <div className="bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 rounded-3xl p-6 text-white shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-start md:items-center gap-4">
          <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-md">
            <Calendar size={32} className="text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-indigo-200 font-bold">שנת לימודים פעילה בניהול</span>
              <span className="px-2.5 py-0.5 bg-white/20 rounded-full text-xs font-black">
                {currentYearObj.name}
              </span>
            </div>
            <h2 className="text-2xl font-black mt-1 tracking-tight">כיתות, מנהלות גן ושיבוץ תלמידים</h2>
            <p className="text-xs text-indigo-100 mt-1 max-w-2xl">
              {selectedYear === '2026-2027' 
                ? 'בשנת הלימודים החדשה תשפ״ז (2026-2027), הכיתות מוצגות עם מנהלות הגן שהוגדרו עבורה ומוכנות לשיבוץ תלמידים ממאגר בית הספר או דרך קובץ אקסל.'
                : 'מוצגים נתוני שנת הלימודים הקודמת (2025-2026).'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 shrink-0">
          <button
            onClick={() => setIsAdding(true)}
            className="inline-flex items-center gap-2 bg-white text-indigo-700 px-5 py-3 rounded-2xl font-bold hover:bg-indigo-50 transition-all shadow-md text-sm"
          >
            <Plus size={18} />
            <span>הוספת כיתה חדשה</span>
          </button>
        </div>
      </div>

      <div className="bg-white p-4 rounded-3xl shadow-sm border border-slate-100 flex items-center gap-3">
        <Search className="text-slate-400" size={20} />
        <input
          type="text"
          placeholder="חיפוש כיתה..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 outline-none text-slate-700 bg-transparent text-sm"
        />
      </div>

      <AnimatePresence>
        {isAdding && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-indigo-50 p-6 rounded-3xl border border-indigo-100"
          >
            <form onSubmit={handleAddClass} className="space-y-4">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="flex-1 space-y-4 w-full">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-indigo-900 mr-2">שם הכיתה</label>
                    <input
                      type="text"
                      value={newClassName}
                      onChange={(e) => setNewClassName(e.target.value)}
                      placeholder="לדוגמה: א'1"
                      className="w-full px-4 py-3 rounded-xl bg-white border border-indigo-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                      autoFocus
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-indigo-900 mr-2">צבע כיתה (יוקרתי)</label>
                    <div className="flex flex-wrap gap-2">
                      {luxuriousColors.map(color => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setNewClassColor(color)}
                          className={`w-8 h-8 rounded-full border-2 transition-all ${newClassColor === color ? 'border-indigo-600 scale-110 shadow-md' : 'border-transparent'}`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                      <input 
                        type="color" 
                        value={newClassColor} 
                        onChange={(e) => setNewClassColor(e.target.value)}
                        className="w-8 h-8 rounded-full overflow-hidden border-none p-0 cursor-pointer"
                      />
                    </div>
                  </div>
                </div>

                <div className="w-full md:w-48 space-y-2">
                  <label className="text-sm font-bold text-indigo-900 mr-2">תמונת כיתה</label>
                  <div className="relative group aspect-square rounded-2xl border-2 border-dashed border-indigo-200 overflow-hidden bg-white flex items-center justify-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/30 transition-all">
                    {newClassImage ? (
                      <img src={newClassImage} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center p-4">
                        <Plus className="mx-auto text-indigo-300 mb-1" size={24} />
                        <span className="text-[10px] text-indigo-400 font-bold uppercase tracking-wider">העלה תמונה</span>
                      </div>
                    )}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => handleImageUpload(e, false)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      title="לחץ להעלאת תמונה"
                    />
                    {newClassImage && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setNewClassImage(null);
                        }}
                        className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-bl-xl opacity-0 group-hover:opacity-100 transition-opacity z-20"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-indigo-100">
                <button
                  type="submit"
                  disabled={actionLoading || !newClassName.trim()}
                  className="bg-indigo-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center gap-2 shadow-lg shadow-indigo-100"
                >
                  {actionLoading ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />}
                  <span>שמירת כיתה</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsAdding(false)}
                  className="bg-white text-slate-600 px-8 py-3 rounded-xl font-bold border border-slate-200 hover:bg-slate-50 transition-all"
                >
                  ביטול
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-20 flex flex-col items-center justify-center text-slate-400">
            <Loader2 className="animate-spin mb-4" size={40} />
            <p className="font-medium">טוען כיתות...</p>
          </div>
        ) : filteredClasses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-right">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-6 py-4 font-bold text-slate-700 text-sm">שם הכיתה</th>
                  <th className="px-6 py-4 font-bold text-slate-700 text-sm">
                    <span>מנהלת גן / גננת</span>
                    <span className="block text-[11px] text-indigo-600 font-medium">({currentYearObj.name})</span>
                  </th>
                  <th className="px-6 py-4 font-bold text-slate-700 text-sm">
                    <span>סייעת</span>
                    <span className="block text-[11px] text-slate-400 font-medium">({currentYearObj.name})</span>
                  </th>
                  <th className="px-6 py-4 font-bold text-slate-700 text-sm">
                    <span>תלמידים רשומים</span>
                    <span className="block text-[11px] text-slate-400 font-medium">({currentYearObj.name})</span>
                  </th>
                  <th className="px-6 py-4 font-bold text-slate-700 text-sm">סטטוס כיתה</th>
                  <th className="px-6 py-4 font-bold text-slate-700 text-sm text-center">פעולות ושיבוץ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredClasses.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4">
                      {editingId === c.id ? (
                        <div className="flex flex-col gap-4 p-2 bg-slate-50 rounded-2xl border border-indigo-100">
                          <div className="flex items-center gap-4">
                            <div className="relative w-20 h-20 rounded-xl border-2 border-dashed border-indigo-200 overflow-hidden bg-white flex items-center justify-center cursor-pointer group hover:border-indigo-400 hover:bg-indigo-50/30 transition-all">
                              {editImage ? (
                                <img src={editImage} alt="Edit Preview" className="w-full h-full object-cover" />
                              ) : (
                                <div className="flex flex-col items-center gap-1">
                                  <Plus className="text-indigo-300" size={24} />
                                  <span className="text-[10px] text-indigo-400 font-bold">החלף</span>
                                </div>
                              )}
                              <input 
                                type="file" 
                                accept="image/*" 
                                onChange={(e) => handleImageUpload(e, true)}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                title="לחץ להעלאת תמונה"
                              />
                              {editImage && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditImage(null);
                                  }}
                                  className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-bl-lg opacity-0 group-hover:opacity-100 transition-opacity z-20"
                                >
                                  <X size={14} />
                                </button>
                              )}
                            </div>
                            <div className="flex-1 space-y-2">
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="w-full px-3 py-1.5 rounded-lg border border-indigo-200 outline-none focus:ring-2 focus:ring-indigo-500"
                                autoFocus
                              />
                              <div className="flex flex-wrap gap-1.5">
                                {luxuriousColors.map(color => (
                                  <button
                                    key={color}
                                    type="button"
                                    onClick={() => setEditColor(color)}
                                    className={`w-6 h-6 rounded-full border transition-all ${editColor === color ? 'border-indigo-600 scale-110' : 'border-transparent'}`}
                                    style={{ backgroundColor: color }}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleUpdateClass(c.id)}
                              className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 flex items-center gap-1.5"
                            >
                              <Check size={16} />
                              <span>עדכן</span>
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="px-4 py-1.5 bg-white text-slate-600 border border-slate-200 rounded-lg font-bold hover:bg-slate-50"
                            >
                              <X size={16} />
                              <span>ביטול</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-12 h-12 rounded-xl overflow-hidden shadow-sm border-2"
                            style={{ borderColor: c.color || '#e2e8f0' }}
                          >
                            {c.imageUrl ? (
                              <img src={c.imageUrl} alt={c.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-slate-100 text-slate-400">
                                <School size={20} />
                              </div>
                            )}
                          </div>
                          <span className="font-bold text-slate-900 text-lg" style={{ color: c.color }}>{c.name}</span>
                        </div>
                      )}
                    </td>

                    {/* Kindergarten Manager Column */}
                    <td className="px-6 py-4">
                      {classConfigs[c.id]?.teacherName ? (
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-indigo-50 text-indigo-700 rounded-lg shrink-0">
                            <UserCheck size={16} />
                          </div>
                          <div>
                            <div className="font-bold text-slate-800 text-sm">{classConfigs[c.id].teacherName}</div>
                            {classConfigs[c.id]?.teacherPhone && (
                              <div className="text-[11px] text-slate-400 flex items-center gap-1 font-mono">
                                <Phone size={10} />
                                <span>{classConfigs[c.id].teacherPhone}</span>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => setStaffModalTarget(c)}
                            className="p-1 text-slate-400 hover:text-indigo-600 rounded-lg hover:bg-slate-100 mr-1"
                            title="ערוך מנהלת גן לשנה זו"
                          >
                            <Edit2 size={13} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setStaffModalTarget(c)}
                          className="px-3 py-1.5 bg-indigo-50/80 hover:bg-indigo-100 text-indigo-700 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all border border-dashed border-indigo-200"
                        >
                          <Plus size={13} />
                          <span>הגדר מנהלת גן</span>
                        </button>
                      )}
                    </td>

                    {/* Assistant Staff Column */}
                    <td className="px-6 py-4 text-xs text-slate-600">
                      {classConfigs[c.id]?.assistantName ? (
                        <div>
                          <div className="font-bold text-slate-700">{classConfigs[c.id].assistantName}</div>
                          {classConfigs[c.id]?.assistantPhone && (
                            <div className="text-[10px] text-slate-400 font-mono">{classConfigs[c.id].assistantPhone}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>

                    {/* Enrolled Students Count Column */}
                    <td className="px-6 py-4">
                      {getEnrolledCount(c.id) > 0 ? (
                        <span className="px-3 py-1.5 bg-emerald-50 text-emerald-700 font-bold rounded-xl text-xs border border-emerald-100 inline-flex items-center gap-1.5">
                          <Users size={13} />
                          <span>{getEnrolledCount(c.id)} תלמידים</span>
                        </span>
                      ) : (
                        <span className="px-3 py-1.5 bg-amber-50 text-amber-700 font-bold rounded-xl text-xs border border-amber-200/60 inline-flex items-center gap-1.5">
                          <Sparkles size={13} />
                          <span>0 תלמידים (ריקה)</span>
                        </span>
                      )}
                    </td>

                    {/* Class Status */}
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleClassStatus(c.id, c.active)}
                        className={`px-4 py-1.5 rounded-xl text-xs font-bold inline-flex items-center gap-1.5 transition-all ${
                          c.active 
                            ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' 
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                      >
                        <div className={`w-1.5 h-1.5 rounded-full ${c.active ? 'bg-emerald-500' : 'bg-slate-400'}`}></div>
                        {c.active ? 'פעילה' : 'לא פעילה'}
                      </button>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setStaffModalTarget(c)}
                          className="px-3 py-1.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-xl text-xs font-bold inline-flex items-center gap-1.5 transition-all"
                          title="הגדרת מנהלת גן וצוות לשנה זו"
                        >
                          <UserCheck size={14} />
                          <span>צוות ומנהלת</span>
                        </button>
                        <button
                          onClick={() => setEnrollModalTarget(c)}
                          className="px-3 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-xl text-xs font-bold inline-flex items-center gap-1.5 transition-all"
                          title="שיבוץ תלמידים ממאגר בית הספר"
                        >
                          <UserPlus size={14} />
                          <span>שיבוץ תלמידים</span>
                        </button>
                        <button
                          onClick={() => {
                            setEditingId(c.id);
                            setEditName(c.name);
                            setEditColor(c.color || '#4f46e5');
                            setEditImage(c.imageUrl || null);
                          }}
                          className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                          title="עריכה"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteClass(c.id)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                          title="מחיקה"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-20 flex flex-col items-center justify-center text-slate-400">
            <div className="p-5 bg-slate-50 rounded-full mb-4">
              <School size={40} />
            </div>
            <p className="font-medium text-lg">לא נמצאו כיתות</p>
            <button
              onClick={() => setIsAdding(true)}
              className="text-indigo-600 font-bold mt-2 hover:underline"
            >
              לחץ כאן להוספת הכיתה הראשונה
            </button>
          </div>
        )}
      </div>

      {/* Staff & Kindergarten Manager Assignment Modal */}
      <ClassStaffModal
        isOpen={!!staffModalTarget}
        targetClass={staffModalTarget}
        currentConfig={staffModalTarget ? classConfigs[staffModalTarget.id] : undefined}
        onClose={() => setStaffModalTarget(null)}
      />

      {/* Enroll Students From Repository Modal */}
      <EnrollStudentsModal
        isOpen={!!enrollModalTarget}
        targetClass={enrollModalTarget}
        onClose={() => setEnrollModalTarget(null)}
        onSuccess={() => {
          fetchStudents();
        }}
      />
    </div>
  );
};

export default Classes;
